import { Add, CreateOutlined } from "@mui/icons-material";
import axios from "axios";
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { Sheet } from "react-modal-sheet";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../../config";
import { collectionPath, EnvContext } from "../../context/EnvContext";
import SessionReplayContext from "../../context/SessionReplayContext";
import { UserContext } from "../../context/UserContext";
import AddressForm from "../../elements/AddressForm";
import SquarePaymentForm from "../../elements/SquarePaymentForm.jsx";
import FAQ from "../../elements/FAQ";
import Footer from "../../elements/Footer";
import Header from "../../elements/Header";
import ShippingCheckoutBanner from "../../elements/ShippingCheckoutBanner";
import ShippingOptions from "../../elements/ShippingOptions";
import Summary from "../../elements/Summary";
import { useFirestore } from "../../firebase/FirestoreContext";
import noop from "../../functions/noop";
import useCart from "../../hooks/useCart";
import useCartValidations from "../../hooks/useCartValidations";
import analyticsHelper from "../../utils/analyticsHelper";
import { formatMoney } from "../../utils/format.js";
import FPButton from "../../elements/FPButton.jsx";
import { getCurrentFacebookClickId } from "../../utils/facebookClickId";
import apiClient from "../../utils/ApiClient";
import AddressBox from "../../elements/AddressBox.jsx";
import StickyFooter from "../../elements/StickyFooter.jsx";
import SavedPaymentMethod from "../../elements/SavedPaymentMethod.jsx";

const CheckoutSquare = () => {
  const { validateCart } = useCartValidations();
  const { summary, appliedDiscount, cartItems, wallet, selectedShippingData, setSelectedShippingData } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const db = useFirestore();
  const firestore = useFirestore();
  const { user, loading } = React.useContext(UserContext);
  const environment = useContext(EnvContext);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const [shippingAddress, setShippingAddress] = useState()
  const [shippingAddressValid, setShippingAddressValid] = useState(false)

  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState(null);
  const [billingAddressValid, setBillingAddressValid] = useState(false);

  const [cardData, setCardData] = useState({ name: '' });
  const [cardError, setCardError] = useState(null);

  // Saved payment methods state
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // null for new card, card id for saved card
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [loadingSavedMethods, setLoadingSavedMethods] = useState(false);

  const [showStickyFooter, setShowStickyFooter] = useState(true);
  const payButtonRef = useRef(null);
  const squarePaymentFormRef = useRef(null);

  const selectedAddress = shippingAddressValid ? shippingAddress : addresses[selectedAddressIndex];

  const { enableSessionReplay } = useContext(SessionReplayContext);

  useEffect(() => {
    enableSessionReplay();
  }, [enableSessionReplay]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [checkoutData, setCheckoutData] = useState({
    cartID: null,
    cartSooperstockID: null,
    items: [],
    subtotal: 0,
    shippingCost: 0,
    freeShippingThreshold: 0,
    appliedDiscount: {
      discountAmount: 0,
      percentage: 0,
      name: "",
    },
    walletBalance: 0,
    minPaymentAmount: 0,
    finalAmount: 0,
    perishableAmount: 0,
  });

  const {
    cartID = null,
    cart_sooperstock_ID = null,
  } = location.state || {};

  const [isOpen, setOpen] = useState(false);
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [tempSelectedAddressIndex, setTempSelectedAddressIndex] = useState(0);

  const handleAddressSelect = (index) => {
    setTempSelectedAddressIndex(index);
  };

  const handleSaveAddressChange = () => {
    setSelectedAddressIndex(tempSelectedAddressIndex);

    if (tempSelectedAddressIndex !== 0) {
      const updatedAddresses = [...addresses];
      const selectedAddress = updatedAddresses[tempSelectedAddressIndex];
      updatedAddresses.splice(tempSelectedAddressIndex, 1);
      updatedAddresses.unshift(selectedAddress);
      setAddresses(updatedAddresses);
      // Since we moved the address to the top, update the index to 0
      setSelectedAddressIndex(0);
    }

    setOpen(false);
  };

  const handleAddNewAddress = () => {
    setOpen(false);
    setShowAddNewForm(true);
  };

  const toggleAddressOptions = () => {
    setTempSelectedAddressIndex(selectedAddressIndex);
    setOpen(true);
  };

  useEffect(() => {
    function checkPayVisibility() {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }
    window.addEventListener('scroll', checkPayVisibility, { passive: true });
    window.addEventListener('resize', checkPayVisibility);

    const timeoutId = setTimeout(checkPayVisibility, 50);

    return () => {
      window.removeEventListener('scroll', checkPayVisibility);
      window.removeEventListener('resize', checkPayVisibility);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [summary, cartItems.length, isLoading, isCheckoutLoading]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsLoading(false);
        setIsCheckoutLoading(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const saveAddress = async (address) => {
    const updatedAddresses = [address, ...addresses];
    setAddresses(updatedAddresses);
    setSelectedAddressIndex(0);

    analyticsHelper.identify(
      { ...user, email: user.email || address.email },
      () => analyticsHelper.addedAddress(address),
    );

    const data = { addresses: updatedAddresses };
    await updateDoc(doc(firestore, "Users/UsersList/DataBase", user.uid), data);

    setShowAddNewForm(false);
  }

  // Fetch saved payment methods for the current user
  const fetchSavedPaymentMethods = async () => {
    if (!user || user.isAnonymous) {
      setLoadingSavedMethods(false);
      return;
    }

    setLoadingSavedMethods(true);
    try {
      const data = await apiClient.getData("/square/payment-methods", user);
      const squarePaymentMethods = data.paymentMethods || [];

      // Transform Square payment method format to match Stripe format
      const transformedPaymentMethods = squarePaymentMethods.map(pm => ({
        id: pm.id,
        card: {
          last4: pm.last4,
          brand: pm.cardBrand.toLowerCase(), // Convert to lowercase to match Stripe format
          exp_month: pm.expMonth,
          exp_year: pm.expYear
        }
      }));

      setSavedPaymentMethods(transformedPaymentMethods);
      // Set first payment method as default if available
      if (transformedPaymentMethods.length > 0) {
        setSelectedPaymentMethod(transformedPaymentMethods[0].id);
      }
    } catch (error) {
      console.error("Error fetching saved payment methods:", error);
    } finally {
      setLoadingSavedMethods(false);
    }
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (paymentMethodId) => {
    setSelectedPaymentMethod(paymentMethodId);
  };

  // Handle payment method deletion
  const handlePaymentMethodDelete = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      await apiClient.delete(`/square/payment-methods/${paymentMethodId}`, user);
      // Remove from local state
      setSavedPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
      // If the deleted method was selected, reset to null
      if (selectedPaymentMethod === paymentMethodId) {
        setSelectedPaymentMethod(null);
      }
    } catch (error) {
      console.error("Error deleting payment method:", error);
      alert("Failed to delete payment method. Please try again.");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await fetchCheckoutData();
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user, location.state?.cartID, firestore, collectionPath]);

  useEffect(() => {
    if (isMounted) return noop;

    if (cartItems.length > 0 && summary.total > 0.01 && wallet) {
      analyticsHelper.startedCheckout({
        cartItems,
        summary,
        cart_sooperstock_ID,
        wallet,
      });
      setIsMounted(true);
    }
  }, [isMounted, cartItems.length, cart_sooperstock_ID, summary.total, wallet]);

  const fetchCheckoutData = async () => {
    try {
      if (!user || !location.state?.cartID) return;

      const cartID = location.state.cartID;

      const cartDocRef = doc(firestore, `${collectionPath}Carts/CartsList/DataBase`, cartID);
      const cartSnapshot = await getDoc(cartDocRef);
      const cartData = cartSnapshot.exists() ? cartSnapshot.data() : {};
      await updateDoc(cartDocRef, {
        opening_checkout_page: new Date(), // Firebase server timestamp
      });

      const walletCollection = collection(firestore, `${collectionPath}Wallets/UserWallets/DataBase`);
      const walletQuery = query(walletCollection, where("user_uid", "==", user.uid));
      const walletSnapshot = await getDocs(walletQuery);
      const totalWalletBalance = walletSnapshot.docs.reduce((acc, doc) => acc + (doc.data().wallet_value || 0), 0);

      const settingsDocRef = doc(firestore, `${collectionPath}SooperstockSettings`, "Wallet");
      const settingsDoc = await getDoc(settingsDocRef);
      const minWalletPayment = settingsDoc.exists() ? settingsDoc.data().minWalletPayment / 100 : 0;

      const shipmentDocRef = doc(firestore, `${collectionPath}SooperstockSettings`, "Shipment");
      const shipmentDoc = await getDoc(shipmentDocRef);
      const shipmentData = shipmentDoc.exists() ? shipmentDoc.data() : {};

      setCheckoutData({
        cartID,
        cartSooperstockID: cartData.cart_sooperstock_ID || null,
        items: cartData.items || [],
        subtotal: cartData.subtotal || 0,
        shippingCost: cartData.shippingCost || 0,
        freeShippingThreshold: shipmentData.freeShipmentThreshold || 0,
        appliedDiscount: {
          discountAmount: cartData.applied_discount_amount || 0,
          percentage: cartData.applied_discount_percentage || 0,
          name: cartData.applied_discount_name || "",
        },
        walletBalance: totalWalletBalance,
        minPaymentAmount: minWalletPayment,
        finalAmount: cartData.finalAmount || 0,
        perishableAmount: cartData.perishableAmount || 0,
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching checkout data:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAddresses = () => {
      if (!loading && user?.addresses?.length) {
        setAddresses(user.addresses);
        setShowAddNewForm(false); // Hide form if addresses exist
      } else if (!loading && (!user?.addresses || user.addresses.length === 0)) {
        setShowAddNewForm(true); // Show form if no addresses
      }
    };

    fetchAddresses();
  }, [firestore, collectionPath, loading, user]);

  // Fetch saved payment methods when user loads
  useEffect(() => {
    if (!loading && user && !user.isAnonymous) {
      fetchSavedPaymentMethods();
    }
  }, [loading, user]);

  const handleWalletOnlyPayment = async () => {
    setIsLoading(true);
    const cartErrors = await validateCart(checkoutData.cartID);

    if (cartErrors.length > 0) {
      navigate('/cart');
      return
    }

    try {
      if (summary.paymentTotals.wallet <= 0) {
        alert("Wallet deduction amount is invalid.");
      }

      let currentEmail = user.email;
      if (!currentEmail) {
        currentEmail = addresses[selectedAddressIndex]?.email;
      }

      if (!currentEmail) {
        alert("Please provide your address and email address to finish order.");
        setIsLoading(false);
        return;
      }

      const shipmentAddress = addresses[selectedAddressIndex];
      if (!shipmentAddress?.email) {
        shipmentAddress.email = currentEmail;
      }

      if (shippingAddress && shippingAddressValid) {
        await saveAddress(shippingAddress);
      }

      await updateDoc(doc(firestore, `${collectionPath}Carts/CartsList/DataBase`, checkoutData.cartID), {
        paymentType: "Wallet",
        walletDeduction: summary.paymentTotals.wallet,
        shipmentAddress: shipmentAddress,
      });

      const payload = {
        cartID: checkoutData.cartID,
        status: "PAID",
        paymentType: "Wallet",
        walletDeduction: summary.paymentTotals.wallet,
        environment,
      };

      const response = await axios.post(`${config.apiConfig.API_ENDPOINT_URL}/payment/walletWebhookPayment`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        navigate(`/payment-success?cart_id=${checkoutData.cartID}&goal=true`, { replace: true });
      } else {
        throw new Error(response.data.error || "Order creation failed.");
      }

      navigate(`/payment-success?cart_id=${checkoutData.cartID}&goal=true`, { replace: true });
    } catch (error) {
      console.error("Wallet payment error:", error);
      alert("Failed to process wallet-only payment.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSquareCardTokenize = async (token, buyer) => {
    setIsCheckoutLoading(true);

    const cartErrors = await validateCart(checkoutData.cartID);

    if (cartErrors.length > 0) {
      navigate('/cart');
      return
    }

    try {
      if (!selectedPaymentMethod && !token) {
        setCardError('Failed to validate card. Please try again.');
        setIsCheckoutLoading(false);
        setIsLoading(false);
        return;
      }

      const userToken = user?.accessToken;
      if (!userToken) {
        setIsCheckoutLoading(false);
        setIsLoading(false);
        alert('User not authenticated.');
        return;
      }

      const facebookClickId = getCurrentFacebookClickId();
      const paymentData = {
        shippingAddress: selectedAddress,
        billingAddress: billingSameAsShipping ? selectedAddress : billingAddress,
        amount: Math.round(summary.paymentTotals.cc * 100), // Cents
        environment,
        ...(selectedPaymentMethod ? { storedCardId: selectedPaymentMethod } : { token: token.token }),
        ...(selectedPaymentMethod === null && savePaymentMethod && { savePaymentMethod: true }),
        metadata: {
          cart_id: checkoutData.cartID,
          discount_code: appliedDiscount?.name || "No Discount Code",
          discount_amount: summary.discountAmount.toFixed(2),
          facebook_click_id: facebookClickId || undefined,
        }
      };

      await saveShippingOption();
      const data = await apiClient.postData("/square/payment", paymentData, user);
      if (!data.success) {
        setIsCheckoutLoading(false);
        setIsLoading(false);
        alert(data.error || "Could not process Square payment");
        return;
      }

      if (shippingAddress && shippingAddressValid) {
        await saveAddress(shippingAddress);
      }

      await saveCartInfo(data.payment)

      if (data.payment && data.payment.status === "COMPLETED") {
        navigate(`/payment-success?cart_id=${checkoutData.cartID}&goal=true`, { replace: true });
      }
      setIsCheckoutLoading(false);
      setIsLoading(false);
    } catch (error) {
      setIsCheckoutLoading(false);
      setIsLoading(false);
      setCardError('There was an issue with checkout. Try again later');
      console.error(error);
    }
  };

  const handleSquareCardElementChange = (event) => {
    setCardError(null);
    if (event && event.type === 'name') {
      setCardData((prev) => ({ ...prev, name: event.value }));
      return;
    }

    if (event && event.type === 'square_input_event') {
      // Handle Square input events if needed
      return;
    }
  };

  const saveShippingOption = async () => {
    if (!selectedShippingData) return;
    const docRef = doc(db, `${collectionPath}Carts/CartsList/DataBase`, checkoutData.cartID);
    await updateDoc(docRef, {
      shippingOption: selectedShippingData.shippingOption || null,
      shippingCost: selectedShippingData.shippingCost || 0,
      free_shipping_threshold: selectedShippingData.shippingOption?.free_threshold || 0,
    });
  };

  const saveCartInfo = async (payment) => {
    try {
      const docRef = doc(db, `${collectionPath}Carts/CartsList/DataBase`, checkoutData.cartID);
      await updateDoc(docRef, {
        squarePaymentId: payment.id,
        shipmentAddress: selectedAddress,
        isAnonymous: user.isAnonymous,
        walletDeduction: summary.paymentTotals.wallet,
        paymentType: "Square",
        shippingCost: selectedShippingData?.shippingCost || 0,
      });
    } catch (error) {
      console.error("Error during payment processing:", error);
      throw new Error(`Error saving cart info: ${error.message}`);
    }
  }


  const disabled = !selectedAddress || isLoading || !!cardError || (!billingSameAsShipping && selectedPaymentMethod === null && !billingAddressValid) || (showAddNewForm && !shippingAddressValid) || (summary.paymentTotals.cc > 1 && selectedPaymentMethod === null && !cardData.name);

  return (
    <div style={{
      backgroundColor: "white"
    }}>
      <Header search={false} />
      <div css={{ maxWidth: "1140px", margin: "0 auto" }}>

        <div style={{ height: 30 }} />
        <ShippingCheckoutBanner totalAmount={summary.subtotal} />

        <div style={styles.checkoutContainer}>
          <h2 style={styles.orderTitle}>Shipping Details</h2>
          {!showAddNewForm && (
            <div style={styles.address}>
              {addresses?.length > 0 ? (
                <>
                  <AddressBox addressObject={addresses[selectedAddressIndex]}>
                    <button style={{ ...styles.linkButton }} onClick={toggleAddressOptions}>
                      <CreateOutlined style={{ fontSize: ".8rem", position: "relative", top: 2 }} /> Change Address
                    </button>
                  </AddressBox>
                </>
              ) : (
                <button style={styles.linkButton} onClick={toggleAddressOptions}>
                  <CreateOutlined style={{ fontSize: ".8rem", position: "relative", top: 2 }} /> Please add an address
                </button>
              )}
            </div>
          )}
          {showAddNewForm && (
            <div>
              <AddressForm
                existingEmail={user?.email}
                onSave={saveAddress}
                onCancel={() => setShowAddNewForm(false)}
                showSaveButton={false}
                showCancelButton={addresses.length > 0}
                onChange={({ address, valid }) => {
                  setShippingAddress(address);
                  setShippingAddressValid(valid);
                }}
              />
            </div>
          )}
          <div>
            <h3 style={{ fontSize: 18 }}>Shipping Speed</h3>
            <ShippingOptions
              cartPrice={summary.subtotal}
              selectedSlug={selectedShippingData?.shippingOption?.slug}
              onChange={setSelectedShippingData}
            />
          </div>

          {summary.paymentTotals.cc > 1 && <>
            {/* Payment Method Selection */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, marginBottom: 16 }}>Payment Information</h3>

              {!user.isAnonymous && loadingSavedMethods && (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                  Loading payment methods...
                </div>
              )}

              {!user.isAnonymous && !loadingSavedMethods && savedPaymentMethods.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  {/* Saved Payment Methods */}
                  {savedPaymentMethods.map((pm) => (
                    <SavedPaymentMethod
                      key={pm.id}
                      pm={pm}
                      selectedPaymentMethod={selectedPaymentMethod}
                      onSelect={handlePaymentMethodSelect}
                      onDelete={handlePaymentMethodDelete}
                    />
                  ))}

                  {/* Add New Card Option */}
                  <div style={{
                    border: selectedPaymentMethod === null ? '2px solid #3B72FE' : '1px solid #ddd',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 12,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }} onClick={() => handlePaymentMethodSelect(null)}>
                    <div style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: selectedPaymentMethod === null ? '2px solid #3B72FE' : '2px solid #ccc',
                      marginRight: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {selectedPaymentMethod === null && (
                        <div style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: '#3B72FE'
                        }} />
                      )}
                    </div>
                    <div style={{ fontWeight: 'bold' }}>Add new payment method</div>
                  </div>
                </div>
              )}

              {/* Show card form when no saved methods or when "add new" is selected */}
              {!user.isAnonymous && !loadingSavedMethods && (selectedPaymentMethod === null || savedPaymentMethods.length === 0) && (
                <div>
                  <SquarePaymentForm
                    ref={squarePaymentFormRef}
                    onChange={handleSquareCardElementChange}
                    onTokenReceived={handleSquareCardTokenize}
                    onError={(error) => setCardError(error)}
                  />
                  {cardError && (
                    <div style={{ color: 'red', marginTop: 8, marginBottom: 8, fontSize: 14 }}>
                      {cardError}
                    </div>
                  )}

                  {/* Save payment method checkbox for new cards */}
                  {!user.isAnonymous && (
                    <div style={{ margin: '16px 0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '15px', gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={savePaymentMethod}
                          onChange={e => setSavePaymentMethod(e.target.checked)}
                          style={{ margin: 0, accentColor: 'black', width: 18, height: 18, verticalAlign: 'middle' }}
                        />
                        <span style={{ lineHeight: 1.2, display: 'inline-block' }}>Save this payment method for future use</span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* For anonymous users, always show the card form */}
              {user.isAnonymous && (
                <div>
                  <SquarePaymentForm
                    ref={squarePaymentFormRef}
                    onChange={handleSquareCardElementChange}
                    onTokenReceived={handleSquareCardTokenize}
                    onError={(error) => setCardError(error)}
                  />
                  {cardError && (
                    <div style={{ color: 'red', marginTop: 8, marginBottom: 8, fontSize: 14 }}>
                      {cardError}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Only show billing address options when using a new payment method */}
            {selectedPaymentMethod === null && (
              <>
                <div style={{ margin: '24px 0 8px 0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', fontSize: '15px', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={billingSameAsShipping}
                      onChange={e => setBillingSameAsShipping(e.target.checked)}
                      style={{ margin: 0, accentColor: 'black', width: 18, height: 18, verticalAlign: 'middle' }}
                    />
                    <span style={{ lineHeight: 1.2, display: 'inline-block' }}>Billing address same as shipping address</span>
                  </label>
                </div>
                {!billingSameAsShipping && (
                  <div style={{ marginBottom: 24 }}>
                    <AddressForm
                      existingEmail={user?.email || selectedAddress.email}
                      onSave={addr => setBillingAddress(addr)}
                      showSaveButton={false}
                      showCancelButton={false}
                      showRecipientsName={false}
                      addressName="Billing Address"
                      onChange={({ address, valid }) => {
                        setBillingAddress(address);
                        setBillingAddressValid(valid);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </>
          }
        </div>
        <div>
          <div style={styles.summary}>
            <Summary summary={summary} appliedDiscount={appliedDiscount} />
            {/** */}
            <div ref={payButtonRef} style={{ height: 30 }} />
            {isLoading || isCheckoutLoading == true ? (
              <FPButton
                fontSize={"1.2rem"}
                padding={"10px"}
                disabled
                $dark>
                Loading...
              </FPButton>
            ) : summary.paymentTotals.cc === 0 ? (
              <FPButton
                $dark
                onClick={handleWalletOnlyPayment}
                disabled={disabled}
                fontSize={"1.2rem"}
                padding={"10px"}
                background={"#3b72fe"}
                outlined={false}
                textColor={"white"}
              >
                {isLoading ? "Processing..." : "Pay"}
              </FPButton>
            ) : (
              <FPButton
                $dark
                onClick={() => {
                  if (selectedPaymentMethod) {
                    // Use saved payment method
                    handleSquareCardTokenize(null, null);
                  } else if (squarePaymentFormRef.current) {
                    // Use new payment method
                    squarePaymentFormRef.current.submit();
                  }
                }}
                disabled={disabled}
                fontSize={"1.2rem"}
                padding={"10px"}
                background={"#3b72fe"}
                outlined={false}
                textColor={"white"}
              >
                {isLoading ? "Processing..." : `Pay with Square`}
              </FPButton>
            )}
          </div>
        </div>

        <FAQ />
      </div>
      <Footer />
      {showStickyFooter && (
        <StickyFooter>
          <div style={{ display: "flex", margin: "10px", width: "100%" }} >
            {isLoading || isCheckoutLoading == true ? (
              <FPButton
                fontSize={"1.2rem"}
                padding={"10px"}
                disabled
                $dark
              >
                Loading...
              </FPButton>
            ) : summary.paymentTotals.cc === 0 ? (
              <FPButton
                $dark
                onClick={handleWalletOnlyPayment}
                disabled={disabled}
                fontSize={"1.2rem"}
                padding={"10px"}
                background={"#3b72fe"}
                outlined={false}
                textColor={"white"}
              >
                {isLoading ? "Processing..." : "Pay"}
              </FPButton>
            ) : (
              <FPButton
                $dark
                onClick={() => {
                  if (selectedPaymentMethod) {
                    // Use saved payment method
                    handleSquareCardTokenize(null, null);
                  } else if (squarePaymentFormRef.current) {
                    // Use new payment method
                    squarePaymentFormRef.current.submit();
                  }
                }}
                disabled={disabled}
                fontSize={"1.2rem"}
                padding={"10px"}
                background={"#3b72fe"}
                outlined={false}
                textColor={"white"}
                width="100%"
              >
                {isLoading ? "Processing..." : `Pay with Square`}
              </FPButton>
            )}
          </div>
        </StickyFooter>
      )}

      {/* Address Modal */}
      <Sheet isOpen={isOpen} onClose={() => setOpen(false)} snapPoints={[0.9]} disableDrag={true}>
        <Sheet.Container style={{}}>
          {" "}
          {/* Added position relative */}
          <div
            onClick={() => setOpen(false)}
            style={{
              cursor: "pointer",
              fontSize: "24px",
              fontWeight: "bold",
              position: "absolute" /* Changed from fixed to absolute */,
              right: 10,
              top: 5,
              zIndex: 10 /* Lower z-index as it's inside the container now */,
              width: 35,
              height: 35,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              lineHeight: "30px",
            }}
          >
            &times;
          </div>
          <Sheet.Content style={{
            overflowY: "auto", maxWidth: "1140px",
            margin: "0 auto", width: "100%"
          }}>
            <Sheet.Header>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  padding: "20px 20px 10px 20px",
                  flexDirection: "column",
                }}
              >
                <div style={{ margin: 0, textAlign: "center", width: "100%", fontSize: "1.2rem", fontWeight: 600 }}>Change Address</div>
                <div style={{ marginTop: 15, width: "100%", fontWeight: 400, fontSize: ".9rem" }}>Select the delivery address:</div>
              </div>
            </Sheet.Header>
            <div style={{ padding: "0 20px" }}>
              {addresses.map((addr, index) => (
                <div key={index} style={{ cursor: "pointer" }} onClick={() => handleAddressSelect(index)}>
                  <AddressBox
                    key={index}
                    addressObject={addr}
                    selected={tempSelectedAddressIndex === index}
                  />
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  style={{
                    backgroundColor: "white",
                    color: "black",
                    padding: "8px 16px",
                    border: "1px solid black",
                    marginTop: "10px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    borderRadius: "50px",
                  }}
                  onClick={handleAddNewAddress}
                >
                  <Add style={{ fontSize: ".9rem", position: "relative", top: 2, marginRight: 5 }} />
                  Add new address
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  style={{
                    backgroundColor: "black",
                    color: "white",
                    padding: "8px 16px",
                    border: "none",
                    width: "100%",
                    margin: "10px auto 30px auto",
                    cursor: "pointer",
                    fontSize: "16px",
                    borderRadius: "50px",
                    maxWidth: "340px",
                  }}
                  onClick={handleSaveAddressChange}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setOpen(false)} />
      </Sheet>
    </div>
  );
};

const styles = {
  checkoutContainer: { padding: "20px", margin: "0 auto", fontFamily: "'Inter', sans-serif" },
  orderTitle: { fontSize: "18px", marginBottom: "20px" },
  address: { marginBottom: "20px" },
  addressBox: { border: "1px solid #ddd", padding: "15px", borderRadius: "12px" },
  headerText: { fontSize: "14px", color: "gray", marginBottom: "10px" },
  nameText: { fontSize: "16px", fontWeight: "bold" },
  addressText: { fontSize: "14px" },
  linkButton: {
    fontSize: ".8rem",
    color: "black",
    cursor: "pointer",
    border: "1px solid black",
    borderRadius: "50px",
    padding: "5px 10px",
    marginTop: "10px",
    backgroundColor: "white",
  },
  total: {
    color: "black",
  },
  summary: {
    marginTop: "10px",
    padding: "10px 15px 40px",
    background: "rgba(59, 114, 254, 0.05)",
  },
};

export default CheckoutSquare;
